import React, { useCallback, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useLanguage, useT } from "../../lib/LanguageContext";
import useAdminLoad, { payloadShapeError } from "../../hooks/useAdminLoad";
import LoadFailure from "../../components/ui/LoadFailure";
import {
  PRIOR_WEIGHT_MAX, PRIOR_WEIGHT_MIN, REVIEW_PATHS, TOKEN_TTL_DAYS_MAX, TOKEN_TTL_DAYS_MIN,
} from "../../reviews/contract";
import { reviewErrorFields, reviewErrorText } from "../../reviews/errorText";
import { fmtDate } from "../../reviews/format";
import { REVIEWS } from "../../constants/testIds/reviews";
import { useScreenWrites } from "../screenWrites";

/**
 * `PUT /super-admin/reviews/program` — the four settings the whole module reads.
 *
 * `seeded_at` IS READ-ONLY AND IT IS THE POINT. The migration writes it once, and
 * `started_at` may never move below it: `started_at` is what the completion sweeper checks
 * before it closes an IN-PERSON window, and a `started_at` in the distant past would
 * mass-complete every historical appointment on the platform and mail a review request for
 * each. The server refuses that with `REVIEW_PROGRAM_INVALID`; this form shows the floor so
 * an operator can see why.
 *
 * A DATE INPUT, AN INSTANT ON THE WIRE. `<input type="date">` speaks `YYYY-MM-DD` and the
 * contract speaks ISO-8601 UTC, so the day is sent as its own midnight IN UTC and read back
 * by slicing the UTC date out of the instant. One direction is the exact inverse of the
 * other, which is what keeps a saved value from drifting a day each time the form is
 * re-opened in a timezone west of Greenwich — the trap of formatting the instant with a
 * LOCAL date formatter instead.
 *
 * NO STEP-UP. Nothing here moves money (the affiliate policy form is gated because it does);
 * the permission is `settings.write` and the write records an audit row.
 *
 * BOUNDS MIRROR THE PYDANTIC ONES EXACTLY (1..50, 1..90). A form laxer than the API only
 * moves the rejection to an opaque refusal; a form stricter than it forbids a legal value.
 */
export default function ReviewProgramForm() {
  const t = useT("superadmin");
  // The program is read with tenants.read and saved with settings.write.
  const { program: canEdit } = useScreenWrites("reviews");
  const tv = useT("common");
  const { lang } = useLanguage();
  const [program, setProgram] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState("");
  const [badFields, setBadFields] = useState([]);

  const load = useCallback(async () => {
    const { data } = await api.get(REVIEW_PATHS.adminProgram);
    if (!data || typeof data !== "object" || typeof data.enabled !== "boolean"
        || !Number.isInteger(data.prior_weight) || !Number.isInteger(data.token_ttl_days)) {
      throw payloadShapeError();
    }
    setProgram(data);
    setForm({
      enabled: data.enabled,
      startedAt: isoDay(data.started_at),
      priorWeight: String(data.prior_weight),
      tokenTtlDays: String(data.token_ttl_days),
    });
  }, []);
  const { loadError, reload } = useAdminLoad(load);

  const inRange = (value, min, max) => {
    const n = Number(value);
    return Number.isInteger(n) && n >= min && n <= max;
  };
  const valid = !!form
    && inRange(form.priorWeight, PRIOR_WEIGHT_MIN, PRIOR_WEIGHT_MAX)
    && inRange(form.tokenTtlDays, TOKEN_TTL_DAYS_MIN, TOKEN_TTL_DAYS_MAX)
    && !!form.startedAt;

  const save = async () => {
    if (!valid || busy) return;
    setBusy(true);
    setRefusal("");
    setBadFields([]);
    try {
      const { data } = await api.put(REVIEW_PATHS.adminProgram, {
        enabled: form.enabled,
        started_at: dayToInstant(form.startedAt),
        prior_weight: Number(form.priorWeight),
        token_ttl_days: Number(form.tokenTtlDays),
      });
      setProgram(data);
      toast.success(t("srev.program.saved"));
    } catch (e) {
      setRefusal(reviewErrorText(e, { t, tv, prefix: "srev", fallback: tv("errors.saveFailed") }));
      setBadFields(reviewErrorFields(e).map((f) => f.field).filter(Boolean));
    } finally {
      setBusy(false);
    }
  };

  if (loadError) {
    return (
      <LoadFailure
        message={reviewErrorText(loadError, { t, tv, prefix: "srev", fallback: tv("errors.loadFailed") })}
        onRetry={reload}
        retryLabel={tv("actions.retry")}
        testId="sa-reviews-program-load-error"
        retryTestId="sa-reviews-program-retry"
      />
    );
  }
  if (!form) return <p className="text-slate-400 text-sm">{t("srev.loading")}</p>;

  return (
    <section className="bg-white rounded-2xl border border-slate-200 p-6" data-testid={REVIEWS.saProgramForm}>
      <h2 className="text-xl text-slate-800">{t("srev.program.title")}</h2>
      <p className="text-xs text-slate-400 mt-1">{t("srev.program.desc")}</p>

      <label className="flex items-center gap-2 text-sm mt-4">
        <input
          type="checkbox"
          checked={form.enabled}
          disabled={!canEdit}
          onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
          data-testid={REVIEWS.saProgramEnabled}
        />
        {t("srev.program.enabled")}
      </label>
      <p className="text-xs text-slate-400 mt-1">{t("srev.program.enabledHint")}</p>

      <div className="grid sm:grid-cols-3 gap-4 mt-4">
        <div>
          <label htmlFor="sa-reviews-started-at" className="block text-sm text-slate-700">
            {t("srev.program.startedAt")}
          </label>
          <input
            id="sa-reviews-started-at"
            type="date"
            value={form.startedAt}
            min={isoDay(program?.seeded_at)}
            disabled={!canEdit}
            onChange={(e) => setForm({ ...form, startedAt: e.target.value })}
            data-testid={REVIEWS.saProgramStartedAt}
            className="mt-1 w-full bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-sm"
          />
          <p className="text-xs text-slate-400 mt-1">{t("srev.program.startedAtHint")}</p>
        </div>
        <div>
          <label htmlFor="sa-reviews-prior-weight" className="block text-sm text-slate-700">
            {t("srev.program.priorWeight")}
          </label>
          <input
            id="sa-reviews-prior-weight"
            type="number"
            min={PRIOR_WEIGHT_MIN}
            max={PRIOR_WEIGHT_MAX}
            value={form.priorWeight}
            disabled={!canEdit}
            onChange={(e) => setForm({ ...form, priorWeight: e.target.value })}
            data-testid={REVIEWS.saProgramPriorWeight}
            className="mt-1 w-full bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-sm"
          />
          <p className="text-xs text-slate-400 mt-1">{t("srev.program.priorWeightHint")}</p>
        </div>
        <div>
          <label htmlFor="sa-reviews-token-ttl" className="block text-sm text-slate-700">
            {t("srev.program.tokenTtl")}
          </label>
          <input
            id="sa-reviews-token-ttl"
            type="number"
            min={TOKEN_TTL_DAYS_MIN}
            max={TOKEN_TTL_DAYS_MAX}
            value={form.tokenTtlDays}
            disabled={!canEdit}
            onChange={(e) => setForm({ ...form, tokenTtlDays: e.target.value })}
            data-testid={REVIEWS.saProgramTokenTtl}
            className="mt-1 w-full bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-sm"
          />
          <p className="text-xs text-slate-400 mt-1">{t("srev.program.tokenTtlHint")}</p>
        </div>
      </div>

      <p className="text-xs text-slate-500 mt-4" data-testid="sa-reviews-program-seeded-at">
        {t("srev.program.seededAt", { at: fmtDate(program?.seeded_at, lang) })}
      </p>

      {refusal && (
        <p
          role="alert"
          data-testid={REVIEWS.saProgramError}
          className="text-sm bg-red-50 border border-red-200 text-red-800 rounded-xl p-3 mt-4"
        >
          {refusal}
          {badFields.length > 0 && <span className="block mt-1 font-mono text-xs">{badFields.join(", ")}</span>}
        </p>
      )}

      {canEdit && (
      <button
        type="button"
        onClick={save}
        disabled={!valid || busy}
        data-testid={REVIEWS.saProgramSave}
        className="mt-4 px-4 h-9 rounded-full bg-slate-800 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {busy ? t("srev.program.saving") : tv("actions.save")}
      </button>
      )}
    </section>
  );
}

/** The UTC calendar day of an ISO instant — `<input type="date">`'s own value shape. */
const isoDay = (iso) => (typeof iso === "string" && iso.length >= 10 ? iso.slice(0, 10) : "");

/** That day's own midnight, in UTC — the exact inverse of `isoDay`. */
const dayToInstant = (day) => new Date(`${day}T00:00:00.000Z`).toISOString();
